import * as React from "react"
import AppBar from "@mui/material/AppBar"
import Box from "@mui/material/Box"
import Toolbar from "@mui/material/Toolbar"
import IconButton from "@mui/material/IconButton"
import Badge from "@mui/material/Badge"
import bellFont from "../../../assets/fonts/bell.svg"
import contactsFont from '../../../assets/fonts/contacts.svg';
import SubjectIcon from '@mui/icons-material/Subject';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import Tooltip from '@mui/material/Tooltip';
import "./AppBar.scss"
import { useDispatch, useSelector } from "react-redux"
import { authTriggerActions } from "../../../backend/store/reducers/authReducer"
import { useNavigate } from "react-router-dom"
import { Notifications } from "../Notifications"
import { actions, notificationsTriggerActions } from '../../../backend/store/reducers/notificationsReducer';
import logo from '../../../assets/images/logo-app.png';
import logoSmall from '../../../assets/images/logo-small.svg';
import {generalActions} from '../../../backend/store/reducers/generalReducer';
import { useTranslation } from "../../../hooks/useTranslation"
import ContactUsModal from "../ContactUsModal/ContactUsModal"
import { DASHBOARD_ROLES, getWorkspaceRole, isVolunteerOnly } from "../../../utils/permissions"
import { ImpersonationTopBarControl, useImpersonation } from "../Impersonation"

export default function AppBarComponent() {  
  const { p } = useTranslation('general');
  const { t } = useTranslation();
  const [isContactModalOpen, setIsContactModalOpen] = React.useState(false)

  const dispatch = useDispatch()
  const navigate = useNavigate()

  React.useEffect(() => {
    dispatch(notificationsTriggerActions.getNotificationsCountTriggerAC());
  }, []);

  
  const storedNotificationsCount = useSelector((state: any) => state.notificationsStore.notificationsCount);
  const openNotifications = useSelector((state: any) => state.notificationsStore.isVisibleNotificationModal);
  const notificationsCount = React.useMemo(() => storedNotificationsCount || null, [storedNotificationsCount]);

  const billingStore = useSelector((state: any) => state.billingStore);
  const currentUser = useSelector((state: any) => state.authStore.currentUser);
  
  const hasActiveSubscription = billingStore.hasActiveSubscription;
  const isTrialActive = billingStore.isTrialActive;
  const isSuperAdmin = currentUser?.role === "super_admin";
  // While an admin views as a user, the phone bar's Exit takes the small logo's place
  // (the bar is otherwise too wide for a 360px phone).
  const { active: isImpersonating } = useImpersonation();

  // Dashboard icon (top bar) is visible to Admin / Anash / Super Admin in the
  // SELECTED workspace -- the same question the /dashboard guard (RoleRoute) asks,
  // including its rule that a super admin's global role admits him anywhere.
  const workspaceRole = getWorkspaceRole(currentUser);
  const showDashboard = isSuperAdmin || (!!workspaceRole && DASHBOARD_ROLES.includes(workspaceRole));

  // Hide notifications if: not super admin AND no active plan AND trial expired.
  // Fix round 1 (volunteering-join report / volunteer-move-notice-report.md):
  // a volunteer-only account's workspace is billing-exempt by design -- no
  // subscription, no trial -- so this predicate always used to read false for
  // him and the bell never rendered at all, even now that the backend holds
  // read_notifications/delete_notifications for that role and persists a
  // VolunteerOffersCancelledNotification he is the one person meant to see.
  // The owner has decided volunteers get the bell; `isVolunteerOnly` is the
  // same predicate `showAccountSettings` below already uses to identify this
  // role, so this stays in sync with it rather than inventing a second check.
  const showNotifications = isSuperAdmin || hasActiveSubscription || isTrialActive || isVolunteerOnly(currentUser);

  // Whole-branch review fix (CRITICAL 1), 2026-09-08: the gear was rendered
  // for every signed-in user, which handed a volunteer-only account -- whose
  // entire app is /volunteering -- a one-tap link into a personal-account page
  // he is not supposed to have. Hidden behind the SAME predicate that now
  // guards the /settings route itself (hocs/accountRoute.tsx), so the icon and
  // the destination can never disagree. Every other role, `visitor` and
  // `limited` included, keeps it: they have their own password, language and
  // support-access cards to manage there.
  const showAccountSettings = !isVolunteerOnly(currentUser);

  // Account settings and Logout are two plain toolbar buttons rather than items
  // in a profile dropdown: both are one tap from anywhere, which matters most on
  // a phone in the field. A stray logout tap is guarded by PendingLogoutDialog,
  // which intercepts when there are unsynced offline writes.
  const logout = () => {
    dispatch(authTriggerActions.logoutUserTriggerAC())
  }

  const handleToggleNotifications = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    dispatch(actions.setVisibilityNotificationModal(true))
  };

  const mobileMenuId = "primary-search-account-menu-mobile"

  return (
      <Box className="appbar" sx={{height: { sx: 56, md: 77 }, position: 'sticky', top: 0, zIndex: 1200}}>
        <AppBar position="relative" sx={{height: { sx: 56, md: 77 }}}>
          <Toolbar sx={{paddingLeft: { md: "69px" }}}>
            <Box>
              <Box sx={{display: {xs: 'flex', md: 'none'}, marginRight: { xs: isImpersonating ? 1 : 2, md: 0 }}}>
                <IconButton
                    size="large"
                    aria-label="show more"
                    aria-controls={mobileMenuId}
                    aria-haspopup="true"
                    onClick={() => dispatch(generalActions.setVisibleSideBar(true))}
                    color="inherit"
                >
                  <SubjectIcon style={{color: '#6A6A99'}}/>
                </IconButton>
              </Box>
            </Box>
            <Box sx={{display: {xs: 'none', md: 'block'}, paddingTop: { md: "11px" }}}>
              <img src={logo} alt="logo" style={{ height: 57, width: "auto" }} />
            </Box>
            <Box sx={{display: {xs: isImpersonating ? 'none' : 'block', md: 'none'}}}>
              <img src={logoSmall} alt="logo"/>
            </Box>
            <Box sx={{flexGrow: 1}}/>
            <ImpersonationTopBarControl />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {showDashboard && (
                <Tooltip title={t("general.dashboard")}>
                  <IconButton
                      size="large"
                      aria-label="dashboard"
                      color="inherit"
                      onClick={() => navigate('/dashboard')}
                  >
                    <DashboardOutlinedIcon sx={{ color: '#6A6A99', fontSize: 22 }} />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton
                  size="large"
                  aria-label="contact us"
                  color="inherit"
                  onClick={() => setIsContactModalOpen(true)}
              >
                <img src={contactsFont} alt="contact" style={{ width: '24px', height: '24px' }} />
              </IconButton>
              {showNotifications && (
                <IconButton
                    size="large"
                    aria-label="show 17 new notifications"
                    color="inherit"
                    onClickCapture={handleToggleNotifications}
                >
                  <Badge badgeContent={notificationsCount} color="error">
                    <img src={bellFont} alt="bell"/>
                  </Badge>
                </IconButton>
              )}
              {showAccountSettings && (
                <Tooltip title={p('account_settings')}>
                  <IconButton
                      size="large"
                      aria-label={p('account_settings')}
                      data-testid="account-settings-menu-item"
                      color="inherit"
                      onClick={() => navigate("/settings")}
                  >
                    <SettingsOutlinedIcon sx={{ color: '#6A6A99', fontSize: 22 }} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={p('logout')}>
                <IconButton
                    size="large"
                    edge="end"
                    aria-label={p('logout')}
                    data-testid="logout-button"
                    color="inherit"
                    onClick={logout}
                >
                  <LogoutOutlinedIcon sx={{ color: '#6A6A99', fontSize: 22 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Toolbar>
        </AppBar>
        {/*{renderMobileMenu}*/}
        {openNotifications && <Notifications/>}
        <ContactUsModal
            open={isContactModalOpen}
            onClose={() => setIsContactModalOpen(false)}
        />
      </Box>
  );
}
